import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { releaseFollowUpAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertTriangle, Clock, CheckCircle2, FileText, Users, Shield,
  ArrowRight, Loader2, Activity, Ticket, Mail, TrendingUp, AlertCircle,
  ClipboardList, CalendarClock,
} from 'lucide-react';
import { ActionCenter } from '@/components/operations/ActionCenter';

const STAGE_CONFIG = {
  Submitted: { color: 'bg-sky-500', label: 'Submitted' },
  In_Progress: { color: 'bg-amber-500', label: 'In Progress' },
  Carrier_Pending: { color: 'bg-violet-500', label: 'Carrier Pending' },
  Missing_Information: { color: 'bg-red-500', label: 'Missing Info' },
  Pending_Release: { color: 'bg-orange-500', label: 'Pending Release' },
  Approved: { color: 'bg-emerald-500', label: 'Approved' },
  Declined: { color: 'bg-red-700', label: 'Declined' },
  On_Hold: { color: 'bg-slate-400', label: 'On Hold' },
  Terminated: { color: 'bg-slate-600', label: 'Terminated' },
  Requested: { color: 'bg-sky-400', label: 'Requested' },
  Pending_Carrier: { color: 'bg-violet-400', label: 'Pending Carrier' },
};

function SummaryCard({ icon: Icon, label, value, color, to }) {
  const inner = (
    <Card className={`relative overflow-hidden transition-all hover:shadow-md ${to ? 'cursor-pointer hover:border-slate-300' : ''}`}>
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold font-heading text-slate-900">{value}</p>
          <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>{inner}</Link> : <div data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>{inner}</div>;
}

function PipelineBar({ pipeline }) {
  const total = pipeline.reduce((s, p) => s + p.count, 0) || 1;
  const ordered = ['Submitted', 'Requested', 'In_Progress', 'Carrier_Pending', 'Pending_Carrier', 'Missing_Information', 'Approved', 'Declined', 'On_Hold', 'Terminated'];
  const sorted = [...pipeline].sort((a, b) => ordered.indexOf(a.stage) - ordered.indexOf(b.stage));

  return (
    <Card data-testid="contract-pipeline">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <TrendingUp size={16} className="text-sky-500" />
          Contract Pipeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex rounded-lg overflow-hidden h-10 mb-4">
          {sorted.map((s) => {
            const cfg = STAGE_CONFIG[s.stage] || { color: 'bg-slate-400', label: s.stage };
            const pct = (s.count / total) * 100;
            if (pct < 1) return null;
            return (
              <div
                key={s.stage}
                className={`${cfg.color} flex items-center justify-center text-white text-[11px] font-medium transition-all`}
                style={{ width: `${pct}%`, minWidth: pct > 5 ? undefined : '24px' }}
                title={`${cfg.label}: ${s.count}`}
              >
                {pct > 8 && s.count}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3">
          {sorted.map((s) => {
            const cfg = STAGE_CONFIG[s.stage] || { color: 'bg-slate-400', label: s.stage };
            return (
              <div key={s.stage} className="flex items-center gap-1.5 text-xs text-slate-600">
                <div className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
                <span>{cfg.label}</span>
                <span className="font-semibold">{s.count}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StaleContractsList({ items }) {
  if (!items.length) return (
    <Card data-testid="stale-contracts">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Clock size={16} className="text-amber-500" /> Follow-Up Tracker
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-6 text-slate-400 text-sm">All contracts are moving — nothing stale!</div>
      </CardContent>
    </Card>
  );

  return (
    <Card data-testid="stale-contracts">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Clock size={16} className="text-amber-500" /> Follow-Up Tracker
          <Badge variant="secondary" className="ml-auto text-xs">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[360px] overflow-y-auto">
        {items.map((item) => (
          <Link key={item.request_id} to={`/requests/${item.request_id}`} className="block">
            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:bg-slate-50 ${
              item.severity === 'critical' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'
            }`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                item.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'
              }`}>
                {item.days_stale}d
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{item.agent_name}</p>
                <p className="text-xs text-slate-500 truncate">{item.carrier} — {item.product}</p>
              </div>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {(item.status || '').replace(/_/g, ' ')}
              </Badge>
              <ArrowRight size={14} className="text-slate-400 shrink-0" />
            </div>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function TicketsList({ items }) {
  return (
    <Card data-testid="open-tickets-list">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Ticket size={16} className="text-rose-500" /> Open Tickets
          <Badge variant="secondary" className="ml-auto text-xs">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[360px] overflow-y-auto">
        {!items.length ? (
          <div className="text-center py-6 text-slate-400 text-sm">No open tickets</div>
        ) : items.slice(0, 10).map((t) => (
          <div key={t.ticket_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-all">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{t.subject}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={t.priority === 'High' ? 'destructive' : t.priority === 'Medium' ? 'default' : 'secondary'} className="text-[10px]">
                  {t.priority || 'Normal'}
                </Badge>
                <span className="text-[10px] text-slate-400">{t.request_type || 'General'}</span>
              </div>
            </div>
            <Badge variant="outline" className="text-[10px]">{(t.status || '').replace(/_/g, ' ')}</Badge>
          </div>
        ))}
        {items.length > 0 && (
          <Link to="/tickets" className="block text-center text-xs text-sky-600 hover:underline pt-2" data-testid="view-all-tickets">
            View all tickets
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function DeadlinesList({ compliance, invitations }) {
  const hasItems = compliance.length > 0 || invitations.length > 0;
  return (
    <Card data-testid="deadlines-list">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Shield size={16} className="text-violet-500" /> Upcoming Deadlines
          {hasItems && <Badge variant="secondary" className="ml-auto text-xs">{compliance.length + invitations.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[360px] overflow-y-auto">
        {!hasItems ? (
          <div className="text-center py-6 text-slate-400 text-sm">No upcoming deadlines</div>
        ) : (
          <>
            {compliance.map((c, i) => (
              <Link key={i} to={`/agents/${c.agent_npn}`} className="block">
                <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:bg-slate-50 ${
                  c.days_until <= 7 ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'
                }`}>
                  <Shield size={16} className={c.days_until <= 7 ? 'text-red-500' : 'text-amber-500'} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.agent_name}</p>
                    <p className="text-xs text-slate-500">{c.requirement} expires in {c.days_until}d</p>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
              </Link>
            ))}
            {invitations.map((inv, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50/50">
                <Mail size={16} className="text-orange-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{inv.email}</p>
                  <p className="text-xs text-slate-500">Invitation expiring — {inv.role_to_assign}</p>
                </div>
              </div>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AgentHealthList({ incomplete, inactive }) {
  const hasItems = incomplete.length > 0 || inactive.length > 0;
  return (
    <Card data-testid="agent-health">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Users size={16} className="text-sky-500" /> Agent Health
          {hasItems && <Badge variant="secondary" className="ml-auto text-xs">{incomplete.length + inactive.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-[360px] overflow-y-auto">
        {!hasItems ? (
          <div className="text-center py-6 text-slate-400 text-sm">All agent profiles are complete</div>
        ) : (
          <Tabs defaultValue="incomplete">
            <TabsList className="w-full mb-3">
              <TabsTrigger value="incomplete" className="flex-1 text-xs">Incomplete ({incomplete.length})</TabsTrigger>
              <TabsTrigger value="inactive" className="flex-1 text-xs">Inactive ({inactive.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="incomplete" className="space-y-2">
              {incomplete.slice(0, 10).map((a, i) => (
                <Link key={i} to={`/agents/${a.npn}`} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-all">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{a.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {a.missing_fields.map((f) => (
                          <Badge key={f} variant="outline" className="text-[10px] text-amber-600 border-amber-300">{f}</Badge>
                        ))}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-slate-400" />
                  </div>
                </Link>
              ))}
            </TabsContent>
            <TabsContent value="inactive" className="space-y-2">
              {inactive.slice(0, 10).map((a, i) => (
                <Link key={i} to={`/agents/${a.npn}`} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-all">
                    <p className="text-sm font-medium text-slate-900">{a.name}</p>
                    <Badge variant="outline" className="text-[10px] text-red-600 border-red-300 ml-auto">Inactive</Badge>
                    <ArrowRight size={14} className="text-slate-400" />
                  </div>
                </Link>
              ))}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

export default function OperationsPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [releaseSummary, setReleaseSummary] = useState(null);

  useEffect(() => {
    api.get('/operations/overview')
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
    // Load release follow-up summary (KR-only endpoint; will silently 403 for others)
    releaseFollowUpAPI.summary()
      .then(res => setReleaseSummary(res.data))
      .catch(() => setReleaseSummary(null));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="operations-loading">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <AlertCircle size={32} className="text-red-400" />
        <p className="text-slate-600">{error}</p>
      </div>
    );
  }

  if (!data) return null;
  const { summary, pipeline, stale_contracts, open_tickets, expiring_compliance, expiring_invitations, incomplete_profiles, inactive_agents, follow_ups = [] } = data;

  return (
    <div className="space-y-6" data-testid="operations-page">
      {/* Header */}
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Operations</p>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Command Center</h1>
        <p className="text-sm text-slate-500 mt-1">Track contracts, follow-ups, compliance, and agent health across your organization.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="summary-cards">
        <SummaryCard icon={FileText} label="Pending Contracts" value={summary.pending_contracts} color="bg-sky-500" to="/requests" />
        <SummaryCard icon={AlertTriangle} label="Follow-Ups" value={summary.follow_ups_open || 0} color={summary.follow_ups_overdue > 0 ? 'bg-red-500' : summary.follow_ups_open > 0 ? 'bg-amber-500' : 'bg-emerald-500'} />
        <SummaryCard icon={Ticket} label="Open Tickets" value={summary.open_tickets} color="bg-rose-500" to="/tickets" />
        <SummaryCard icon={Shield} label="Compliance Due" value={summary.expiring_compliance} color={summary.expiring_compliance > 0 ? 'bg-violet-500' : 'bg-emerald-500'} />
        <SummaryCard icon={Users} label="Incomplete Profiles" value={summary.incomplete_profiles} color={summary.incomplete_profiles > 0 ? 'bg-amber-500' : 'bg-emerald-500'} />
      </div>

      {/* Pipeline */}
      <PipelineBar pipeline={pipeline} />

      {/* KR Action Center — 6 widgets */}
      {isKR && <ActionCenter />}

      {/* Release Follow-Ups banner */}
      {isKR && releaseSummary && (releaseSummary.open_total > 0 || releaseSummary.overdue > 0) && (
        <Card className={`border-l-4 ${releaseSummary.overdue > 0 ? 'border-l-red-500 bg-red-50/30' : releaseSummary.today > 0 ? 'border-l-amber-500 bg-amber-50/30' : 'border-l-orange-400 bg-orange-50/20'}`} data-testid="operations-release-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <CalendarClock size={28} className={releaseSummary.overdue > 0 ? 'text-red-500' : 'text-orange-500'} />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-slate-900">Release Follow-Ups</p>
                <div className="flex flex-wrap gap-4 mt-1 text-xs text-slate-600">
                  {releaseSummary.overdue > 0 && <span><strong className="text-red-600">{releaseSummary.overdue}</strong> overdue</span>}
                  {releaseSummary.today > 0 && <span><strong className="text-amber-600">{releaseSummary.today}</strong> due today</span>}
                  {releaseSummary.due_soon > 0 && <span><strong className="text-orange-600">{releaseSummary.due_soon}</strong> due in 7 days</span>}
                  <span><strong>{releaseSummary.open_total}</strong> total open</span>
                </div>
              </div>
              <Link to="/release-followups" data-testid="open-release-queue">
                <Button variant="outline" size="sm">
                  Open Queue <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Follow-Ups Section */}
      {follow_ups.length > 0 && (
        <Card data-testid="operations-followups">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <ClipboardList size={16} className="text-sky-500" /> Active Follow-Ups
              <Badge variant="secondary" className="ml-auto text-xs">{summary.follow_ups_open || follow_ups.length}</Badge>
              {summary.follow_ups_overdue > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{summary.follow_ups_overdue} overdue</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
            {follow_ups.map(fu => {
              const isOverdue = fu.due_date && fu.due_date < new Date().toISOString().split('T')[0];
              const typeIcons = { contract_review: <FileText size={13} className="text-sky-500" />, missing_info: <AlertTriangle size={13} className="text-amber-500" />, ticket_response: <Ticket size={13} className="text-blue-500" /> };
              return (
                <div key={fu.followup_id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50/50' : fu.priority === 'high' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}>
                  {typeIcons[fu.type] || <ClipboardList size={13} className="text-slate-500" />}
                  <div className="flex-1 min-w-0">
                    {fu.link_url ? (
                      <Link to={fu.link_url} className="text-sm font-medium text-slate-800 hover:text-sky-700 hover:underline truncate block">{fu.title}</Link>
                    ) : (
                      <p className="text-sm font-medium text-slate-800 truncate">{fu.title}</p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400">{fu.type?.replace(/_/g, ' ')}</span>
                      {fu.due_date && <span className={`text-[10px] ${isOverdue ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>Due: {fu.due_date}</span>}
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${fu.priority === 'high' ? 'bg-red-100 text-red-700' : fu.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{fu.priority}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StaleContractsList items={stale_contracts} />
        <TicketsList items={open_tickets} />
        <DeadlinesList compliance={expiring_compliance} invitations={expiring_invitations} />
        <AgentHealthList incomplete={incomplete_profiles} inactive={inactive_agents} />
      </div>
    </div>
  );
}
